import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { brainScopeName, type BrainConversationSummary } from '../../../../shared/types/brain';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface BrainConversationSidebarProps {
  activeConversationId?: string;
  /** Called after any navigation (close the mobile Sheet). */
  onNavigate?: () => void;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function groupKey(lastMessageAt: string): 'today' | 'last7' | 'older' {
  const age = Date.now() - new Date(lastMessageAt).getTime();
  if (new Date(lastMessageAt).toDateString() === new Date().toDateString()) return 'today';
  if (age < 7 * DAY_MS) return 'last7';
  return 'older';
}

/** Una página de listConversations (el backend pagina por cursor de Dynamo). */
type ConversationsPage = { conversations: BrainConversationSummary[]; nextCursor?: Record<string, unknown> | null };

export function BrainConversationSidebar({ activeConversationId, onNavigate }: BrainConversationSidebarProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.brain.listConversations.useInfiniteQuery(
      {},
      { getNextPageParam: (lastPage: ConversationsPage) => lastPage.nextCursor ?? undefined }
    );
  const renameMutation = trpc.brain.renameConversation.useMutation();
  const deleteMutation = trpc.brain.deleteConversation.useMutation();

  const [clientFilter, setClientFilter] = useState<string>('all');
  const [renaming, setRenaming] = useState<BrainConversationSummary | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState<BrainConversationSummary | null>(null);

  const conversations: BrainConversationSummary[] = data?.pages.flatMap((p: ConversationsPage) => p.conversations) ?? [];

  // Distinct clients present in the user's history (for the filter)
  const clientOptions = useMemo(
    () => [...new Set(conversations.map((c) => c.clientId))],
    [conversations]
  );

  const filtered = clientFilter === 'all'
    ? conversations
    : conversations.filter((c) => c.clientId === clientFilter);

  const groups: Array<{ key: 'today' | 'last7' | 'older'; label: string; items: BrainConversationSummary[] }> = [
    { key: 'today', label: t('brain.sidebar.today'), items: [] },
    { key: 'last7', label: t('brain.sidebar.last7'), items: [] },
    { key: 'older', label: t('brain.sidebar.older'), items: [] },
  ];
  for (const c of filtered) groups.find((g) => g.key === groupKey(c.lastMessageAt))!.items.push(c);

  const goTo = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const handleRename = async () => {
    if (!renaming || !renameValue.trim()) return;
    try {
      await renameMutation.mutateAsync({
        conversationId: renaming.conversationId,
        title: renameValue.trim(),
      });
      utils.brain.listConversations.invalidate();
    } catch {
      toast.error(t('brain.sidebar.action_error'));
    }
    setRenaming(null);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    const wasActive = deleting.conversationId === activeConversationId;
    try {
      await deleteMutation.mutateAsync({ conversationId: deleting.conversationId });
      utils.brain.listConversations.invalidate();
      if (wasActive) goTo('/brain');
    } catch {
      toast.error(t('brain.sidebar.action_error'));
    }
    setDeleting(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* New chat */}
      <div className="p-3 space-y-2">
        <Button
          variant="outline"
          className="w-full justify-start gap-2 text-sm"
          onClick={() => goTo('/brain')}
        >
          <MessageSquarePlus className="w-4 h-4" />
          {t('brain.sidebar.new_chat')}
        </Button>

        {clientOptions.length > 1 && (
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-full h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('brain.sidebar.all_clients')}</SelectItem>
              {clientOptions.map((id) => (
                <SelectItem key={id} value={id}>
                  {brainScopeName(id)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Conversation list */}
      <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-3">
        {isLoading ? (
          <p className="text-xs text-muted-foreground px-2 py-4">…</p>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground px-2 py-4">{t('brain.sidebar.empty')}</p>
        ) : (
          groups.filter((g) => g.items.length > 0).map((group) => (
            <div key={group.key} className="mb-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1.5">
                {group.label}
              </p>
              {group.items.map((c) => (
                <div
                  key={c.conversationId}
                  className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${
                    c.conversationId === activeConversationId
                      ? 'bg-primary/10 text-foreground'
                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                  onClick={() => goTo(`/brain/c/${c.conversationId}`)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{c.title}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {brainScopeName(c.clientId)}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <button
                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded hover:bg-background transition-opacity shrink-0"
                        aria-label={t('brain.sidebar.actions')}
                      >
                        <MoreHorizontal className="w-3.5 h-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem
                        onClick={() => {
                          setRenameValue(c.title);
                          setRenaming(c);
                        }}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-2" />
                        {t('brain.sidebar.rename')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDeleting(c)}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" />
                        {t('brain.sidebar.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          ))
        )}
        {!isLoading && hasNextPage && (
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="w-full text-xs text-muted-foreground hover:text-foreground py-2 disabled:opacity-50 transition-colors"
          >
            {isFetchingNextPage ? t('brain.sidebar.loading_more') : t('brain.sidebar.load_more')}
          </button>
        )}
      </div>

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={(open) => !open && setRenaming(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('brain.sidebar.rename_title')}</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            maxLength={120}
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)}>
              {t('brain.chat.cancel')}
            </Button>
            <Button onClick={handleRename} disabled={!renameValue.trim() || renameMutation.isPending}>
              {t('brain.sidebar.rename')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('brain.sidebar.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('brain.sidebar.delete_confirm_body', { title: deleting?.title ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('brain.chat.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              {t('brain.sidebar.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
